import React, { createContext, useState, useContext, useEffect, useMemo } from "react";
import axios from "axios";
import { getToken } from "../utils/tokenUtils";

// Create the EmployeeApproverMasterContext
const EmployeeApproverMasterContext = createContext();

// Custom hook to use the EmployeeApproverMasterContext
export const useEmployeeApprover = () => useContext(EmployeeApproverMasterContext);

// Provider component to wrap the app
export const EmployeeApproverMasterProvider = ({ children }) => {
  const [employeeApprovers, setEmployeeApprovers] = useState([]);
  const API_URL = "https://hrmsnew.prepstripe.com/api/employee-approvers"; 
  const token = getToken();
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    perPage: 15,
  });
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchEmployeeApprovers = async (page = 1) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}?page=${page}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      // setDepartments(response.data?.data || {}); // Access `data.data` for actual approver module master
      const { data, current_page, last_page, per_page, total } = response.data;

      setEmployeeApprovers(data.data || []); // Update leave types list

      setPagination({
          currentPage: response.data.data.current_page,
          totalPages: response.data.data.last_page,
          totalItems: response.data.data.total,
          perPage: response.data.data.per_page,
      });
      setLoading(false);
    } catch (error) {
      console.error("Error fetching employee approve module master:", error);
      setError(error.response?.data?.message || "Failed to fetch employee approver.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch employee approver master from the API on load
  useEffect(() => {
    fetchEmployeeApprovers();
  }, [API_URL, token]);

  // Add a new employee approver
  const addEmployeeApprover = async (newEmployeeApprover) => {
    try {
      const response = await axios.post(API_URL, newEmployeeApprover, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setEmployeeApprovers((prev) => [response.data?.data, ...prev]);
    } catch (error) {
      console.error("Error adding employee approver module:", error);
    }
  };

  // Update an existing employee approver
  const updateEmployeeApprover = async (updatedEmployeeApprover) => {
    try {
      const response = await axios.put(`${API_URL}/${updatedEmployeeApprover.id}`, updatedEmployeeApprover, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setEmployeeApprovers((prev) =>
        prev.map((employeeApprover) =>
          employeeApprover.id === updatedEmployeeApprover.id ? { ...employeeApprover, ...response.data?.data } : employeeApprover
        )
      );
    } catch (error) {
      console.error("Error updating employee approver master:", error);
    }
  };

  // Delete a employee approver
  const deleteEmployeeApprover = async (id) => {
    try {
      await axios.delete(`${API_URL}/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setEmployeeApprovers((prev) => prev.filter((employeeApprover) => employeeApprover.id !== id));
    } catch (error) {
      console.error("Error deleting employee approver:", error);
    }
  };

  // Memoize the context value
  const value = useMemo(
    () => ({
      isLoading,
      error,
      pagination,
      employeeApprovers,
      fetchEmployeeApprovers,
      addEmployeeApprover,
      updateEmployeeApprover,
      deleteEmployeeApprover,
    }),
    [employeeApprovers, isLoading] 
  );

  return <EmployeeApproverMasterContext.Provider value={value}>{children}</EmployeeApproverMasterContext.Provider>;
};
